import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ToastService } from './toast.service';

export interface Challenger {
	_id: string;
	username: string;
}

export interface Competitor {
	_id: string;
	username: string;
}

export interface Duel {
	_id?: string;
	type?: 'classic' | 'guessing';
	secret_id?: string;
	description?: string;
	challenger?: string | Challenger;
	competitor?: string | Competitor | null;
	outcome_challenger?: string;
	outcome_competitor?: string;
	amount?: number;
	paypal?: string;
	time_to_accept?: Date;
	expiration?: Date;
	status?: string;
	winner?: {
		result_challenger: string;
		result_competitor: string;
	};
}

export interface DuelPopulated extends Duel {
	challenger: Challenger;
	competitor: Competitor;
}

@Injectable({
	providedIn: 'root',
})
export class DuelService {
	private restServerUrl;
	duels: Duel[] = [];
	duelUpdated = new Subject<Duel[]>();
	duelBadgeCount = new BehaviorSubject<number>(0);
	currentDuelBadgeCount = this.duelBadgeCount.asObservable();

	constructor(
		private http: HttpClient,
		private toastService: ToastService,
	) {
		const protocol = environment.ssl ? 'https://' : 'http://';
		this.restServerUrl = protocol + environment.restServer + ':' + environment.restServerPort;
	}

	getDuelUpdateListener() {
		return this.duelUpdated.asObservable();
	}

	getDuelList() {
		this.http
			.get<{ msg: 'users duel list'; duels: Duel[] }>(this.restServerUrl + '/api/duel/list')
			.pipe(map(data => data.duels))
			.subscribe({
				next: data => {
					this.duels = data;
					this.duelUpdated.next([...this.duels]);
				},
				error: () => {
					this.toastService.showToastOnError('Keine Verbindung zum Server');
				},
			});
	}

	getDuelById(secretId: string) {
		return this.http.get<{ msg: 'duel read'; duel: DuelPopulated }>(this.restServerUrl + '/api/duel/read/' + secretId);
	}

	getChallengeById(id: string) {
		return this.http.get<{ msg: 'challenge'; duel: Duel }>(this.restServerUrl + '/api/duel/challenge/' + id);
	}

	createDuel(
		type: 'classic' | 'guessing',
		description: string,
		challenger: string,
		outcome_challenger: string | undefined,
		outcome_competitor: string | undefined,
		amount: number,
		time_to_accept: Date,
		expiration: Date,
		competitor: string | null,
	) {
		const newDuel: Duel = {
			type,
			description,
			challenger,
			outcome_challenger,
			outcome_competitor,
			amount,
			time_to_accept,
			expiration,
			competitor,
		};
		return this.http.post<{ msg: string; duel: Duel }>(this.restServerUrl + '/api/duel/create', newDuel);
	}

	updateDuel(duel: Duel) {
		return this.http.put<Duel>(this.restServerUrl + '/api/duel/update/' + duel._id, duel);
	}

	challengeAccept(duel: Duel) {
		return this.http.post<{ msg: 'duel accepted'; duel: DuelPopulated }>(
			this.restServerUrl + '/api/duel/accept/' + duel.secret_id,
			{
				outcome_competitor: duel.outcome_competitor,
			},
		);
	}

	challengeDecline(duel: Duel) {
		return this.http.get<Duel>(this.restServerUrl + '/api/duel/decline/' + duel.secret_id);
	}

	challengeImprove(data: { secret_id: string; amount: number }) {
		const challengeData: Duel = {
			secret_id: data.secret_id,
			amount: data.amount,
		};
		return this.http.post<{ newChallenge: Duel }>(this.restServerUrl + '/api/duel/improve', challengeData).pipe(
			map(res => {
				return res.newChallenge;
			}),
		);
	}

	resolveDuel(winner: string, duelId: string) {
		const data = {
			winner: winner,
			duelId: duelId,
		};
		return this.http.post<{
			msg: string;
			status: string;
			duel: Duel;
		}>(this.restServerUrl + '/api/duel/resolve', data);
	}

	challengeDelete(id: string) {
		return this.http.delete(this.restServerUrl + '/api/duel/delete/' + id).pipe(
			tap(() => {
				const duels = this.duels.splice(
					this.duels.findIndex(item => {
						return item._id === id;
					}),
					1,
				);
				this.duelUpdated.next([...duels]);
			}),
		);
	}

	transcribeVoiceForDuel(file: File) {
		const formData = new FormData();
		formData.append('voice', file, file.name);
		return this.http.post<{
			transcription: string;
			duel: {
				type?: 'classic' | 'guessing';
				description?: string;
				outcome_challenger?: string;
				outcome_competitor?: string;
				amount?: number;
				time_to_accept?: string;
			} | null;
		}>(this.restServerUrl + '/api/duel/from-voice', formData);
	}

	increaseDuelBadgeCount() {
		const newcount: number = this.duelBadgeCount.value;
		this.duelBadgeCount.next(newcount + 1);
	}

	resetDuelBadgeCount() {
		this.duelBadgeCount.next(0);
	}
}
